/**
 * Avatec Marine E-mail Signature Template
 *
 * Cross-client compatible HTML email signature generator.
 * Tested for: Outlook Desktop 2007-2024, New Outlook, Gmail (web/mobile),
 * Apple Mail, Thunderbird, Yahoo Mail, Outlook.com, Samsung Mail, Android default mail.
 */

import { getLink } from '../helpers';

// Default values for Avatec template
const AVATEC_DEFAULTS = {
  company: 'Avatec Marine',
  additionalText: '',
  websiteUrl: 'https://www.avatecmarine.com',
  websiteDisplay: 'www.avatecmarine.com',
  logoUrl: '/logo-avatec.png'
};

/**
 * Generate HTML for Avatec Marine email signature template
 * @param {Object} formData - User input data for the email signature
 * @param {Object} switchStates - State of toggle switches for optional content
 * @returns {string} Generated HTML
 */
const generateAvatecTemplate = (formData, switchStates) => {
  // Determine which logo source to use
  const logoSource = formData.logoType === 'base64'
    ? formData.logoBase64
    : formData.logoUrl;

  // Count visible rows in the right column
  const visibleRightRows = [
    formData.rightRow1.label.trim() !== '' || formData.rightRow1.value.trim() !== '',
    formData.rightRow2.label.trim() !== '' || formData.rightRow2.value.trim() !== '',
    formData.rightRow3.label.trim() !== '' || formData.rightRow3.value.trim() !== '',
    formData.rightRow4.label.trim() !== '' || formData.rightRow4.value.trim() !== ''
  ].filter(Boolean).length;

  // Count visible rows in the middle column (personal info)
  const visibleMiddleRows = [
    formData.fullName.trim() !== '',
    formData.position.trim() !== '',
    formData.company.trim() !== '',
    formData.additionalText.trim() !== ''
  ].filter(Boolean).length;

  // Logo width based on maximum rows in a single column
  const realVisibleRows = Math.max(visibleRightRows, visibleMiddleRows);
  let logoWidth = 85;
  if (realVisibleRows <= 3) {
    logoWidth = 60;
  }

  // Convert hex color to RGB for better Outlook compatibility
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    return result ?
      `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})` :
      hex;
  };

  const rgbTextColor = hexToRgb(formData.textColor);

  return `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <meta name="format-detection" content="telephone=no"/>
  <!--[if !mso]><!-->
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <!--<![endif]-->
  <title>Email Signature</title>

  <!--[if mso]>
  <style type="text/css">
    table {border-collapse:collapse !important;border-spacing:0 !important;margin:0 !important;}
    span, td, strong, a {color:${rgbTextColor} !important; mso-color-alt:${formData.textColor} !important; font-family: 'Calibri', 'Arial', 'Helvetica', 'sans-serif' !important;}
  </style>
  <noscript>
  <xml>
    <o:OfficeDocumentSettings>
      <o:PixelsPerInch>96</o:PixelsPerInch>
      <o:AllowPNG/>
    </o:OfficeDocumentSettings>
  </xml>
  </noscript>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%; background-color: #FFFFFF;" bgcolor="#FFFFFF">
  <!--[if mso]>
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="640" align="center" style="width:640px;">
    <tr>
      <td style="line-height:0px;font-size:0px;mso-line-height-rule:exactly;">
  <![endif]-->
  <div style="font-family: 'Calibri', 'Arial', 'Helvetica', 'sans-serif'; font-size: 12px; color: ${rgbTextColor}; max-width: 640px;">
    <!-- Main Info Table -->
    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="640" style="border-collapse: collapse; width: 640px;">
      <tr style="vertical-align: top;">
        <!-- Logo Column -->
        <td width="90" valign="top" style="width: 90px; vertical-align: top; padding-right: 15px; padding-top: 0px; line-height: 14px; mso-line-height-rule: exactly;">
          <a href="${formData.websiteUrl}" target="_blank" style="text-decoration: none;">
            <img
              alt="${formData.company} Logo"
              style="width:${logoWidth}px; height:auto; border:0; display: block; -ms-interpolation-mode: bicubic; outline: none;"
              src="${logoSource}"
              width="${logoWidth}"
              border="0"
            />
          </a>
        </td>

        <!-- Vertical Line 1 -->
        <td width="1" valign="top" style="width: 1px; padding: 0; font-size: 0; line-height: 0; mso-line-height-rule: exactly; background-color: #0061a1;" bgcolor="#0061a1">
          <div style="width: 1px; font-size: 0; line-height: 0;">&#xA0;</div>
        </td>

        <!-- Personal Info Column -->
        <td
          width="165"
          valign="top"
          style="
            width: 165px;
            vertical-align: top;
            padding-left: 15px;
            padding-right: 15px;
            line-height: 14px;
            mso-line-height-rule: exactly;
            color: ${rgbTextColor};
          ">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="165" style="border-collapse: collapse; width: 165px;">
            <tr>
              <td style="font-weight: bold; padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  ${formData.fullName}
                </font>
              </td>
            </tr>
            <tr>
              <td style="font-weight: bold; padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  ${formData.position}
                </font>
              </td>
            </tr>
            <tr>
              <td style="padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  ${formData.company}
                </font>
              </td>
            </tr>
            <tr>
              <td style="font-style: italic; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  ${formData.additionalText}
                </font>
              </td>
            </tr>
          </table>
        </td>

        <!-- Vertical Line 2 -->
        <td width="1" valign="top" style="width: 1px; padding: 0; font-size: 0; line-height: 0; mso-line-height-rule: exactly; background-color: #0061a1;" bgcolor="#0061a1">
          <div style="width: 1px; font-size: 0; line-height: 0;">&#xA0;</div>
        </td>

        <!-- Contact Info Column -->
        <td
          width="250"
          valign="top"
          style="
            width: 250px;
            vertical-align: top;
            padding-left: 15px;
            line-height: 14px;
            mso-line-height-rule: exactly;
            color: ${rgbTextColor};
          ">
          <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="250" style="border-collapse: collapse; width: 250px;">
            ${formData.rightRow1.label.trim() !== '' || formData.rightRow1.value.trim() !== '' ? `
            <tr>
              <td style="padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.rightRow1.label}:</strong>
                  <a href="${getLink(formData.rightRow1.value, formData.rightRow1.label).href}" style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor}; text-decoration: none;"><font color="${formData.textColor}">${formData.rightRow1.value}</font></a>
                </font>
              </td>
            </tr>
            ` : ''}
            ${formData.rightRow2.label.trim() !== '' || formData.rightRow2.value.trim() !== '' ? `
            <tr>
              <td style="padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.rightRow2.label}:</strong>
                  <a href="${getLink(formData.rightRow2.value, formData.rightRow2.label).href}" style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor}; text-decoration: none;"><font color="${formData.textColor}">${formData.rightRow2.value}</font></a>
                </font>
              </td>
            </tr>
            ` : ''}
            ${formData.rightRow3.label.trim() !== '' || formData.rightRow3.value.trim() !== '' ? `
            <tr>
              <td style="padding-bottom: 4px; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.rightRow3.label}:</strong>
                  <a href="${getLink(formData.rightRow3.value, formData.rightRow3.label).href}" style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor}; text-decoration: none;"><font color="${formData.textColor}">${formData.rightRow3.value}</font></a>
                </font>
              </td>
            </tr>
            ` : ''}
            ${formData.rightRow4.label.trim() !== '' || formData.rightRow4.value.trim() !== '' ? `
            <tr>
              <td style="line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
                <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
                  <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.rightRow4.label}:</strong>
                  <a href="${getLink(formData.rightRow4.value, formData.rightRow4.label).href}" style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor}; text-decoration: none;"><font color="${formData.textColor}">${formData.rightRow4.value}</font></a>
                </font>
              </td>
            </tr>
            ` : ''}
          </table>
        </td>
      </tr>
    </table>

    <!-- Spacing -->
    <table role="presentation" width="640" cellspacing="0" cellpadding="0" border="0" style="border-collapse: collapse; width: 640px;">
      <tr>
        <td height="10" style="height: 10px; font-size: 0; line-height: 0;">&#xA0;</td>
      </tr>

    ${switchStates.hubs ? `
    <!-- Hubs Section -->
      <tr>
        <td style="line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
          <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
            <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">Hubs:</strong> <span style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.hubs}</span>
          </font>
        </td>
      </tr>

    <!-- Spacing -->
      <tr>
        <td height="10" style="height: 10px; font-size: 0; line-height: 0;">&#xA0;</td>
      </tr>
    ` : ''}

    ${switchStates.disclaimer ? `
    <!-- Disclaimer Section -->
      <tr>
        <td style="text-align: justify; mso-text-justify: inter-ideograph; line-height: 14px; mso-line-height-rule: exactly; color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">
          <font face="Calibri, Arial, Helvetica, sans-serif" color="${formData.textColor}">
            <strong style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">Disclaimer:</strong> <span style="color: ${rgbTextColor}; mso-color-alt: ${formData.textColor};">${formData.disclaimer}</span>
          </font>
        </td>
      </tr>
    ` : ''}
    </table>
  </div>
  <!--[if mso]>
      </td>
    </tr>
  </table>
  <![endif]-->
</body>
</html>`;
};

// Export both the template generator and its default values
export { AVATEC_DEFAULTS };
export default generateAvatecTemplate;
